import { createFileRoute, notFound } from "@tanstack/react-router";
import { type Tenant, useTenant } from "albertine-shared-web";
import type { MouseEvent } from "react";
import { isEmbeddedInParentFrame } from "@/lib/parentFrame";
import { EligibilityFlow } from "@/shared/eligibility";
import {
    postEligibilityMessageToParent,
    useNotifyParentEligibilityReady,
} from "./-hooks/useEligibilityParentFrame";

export const Route = createFileRoute("/eligibility/")({
    beforeLoad: ({ search }) => {
        if (search.tenant !== "mastercard") {
            throw notFound();
        }

        document.documentElement.style.setProperty(
            "--color-body-top",
            "var(--color-background-onboarding)",
        );
        document.documentElement.style.setProperty(
            "--color-body-bottom",
            "var(--color-background-onboarding)",
        );
    },

    component: RouteComponent,
    head: ({ match }) => {
        const { tenant, locale } = match.search;
        return {
            meta: [
                {
                    title: `Eligibility - ${tenant} ${locale}`,
                },
            ],
        };
    },
    onError: () => {
        throw notFound();
    },
});

function RouteComponent() {
    const { tenant, locale } = Route.useSearch();
    const { tenantConfig } = useTenant();
    const isEmbeddedInBrowserHost = isEmbeddedInParentFrame();

    useNotifyParentEligibilityReady();

    function supportEmailClickHandler(event: MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        event.stopPropagation();

        if (isEmbeddedInBrowserHost) {
            postEligibilityMessageToParent({ type: "eligibility.openEmail" });
            return;
        }

        window.location.href = `mailto:${tenantConfig.emailService}`;
    }

    return (
        <div className="relative mx-auto flex h-full min-h-0 w-full max-w-small-content flex-col px-24 py-safe">
            {isEmbeddedInBrowserHost ? (
                <EligibilityFlow
                    tenant={tenant as Tenant}
                    locale={locale}
                    supportEmailClickHandler={supportEmailClickHandler}
                    onEligibilityVerified={() => {
                        postEligibilityMessageToParent({
                            type: "eligibility.verified",
                        });
                    }}
                    onSendVerificationCodeCallback={({ phoneNumber }) => {
                        postEligibilityMessageToParent({
                            type: "eligibility.sendCode",
                            phoneNumber,
                        });
                    }}
                    onExitFlow={() => {
                        postEligibilityMessageToParent({ type: "eligibility.close" });
                    }}
                />
            ) : (
                <EligibilityFlow
                    tenant={tenant as Tenant}
                    locale={locale}
                    supportEmailClickHandler={
                        import.meta.env.DEV ? supportEmailClickHandler : undefined
                    }
                />
            )}
        </div>
    );
}
